import type { ReactNode } from 'react';

import {
  type AvatarProps,
  type ComparisonField,
  type DropdownOption,
  type FilterElement,
  type MultiSelectOption,
  type TableFilter,
  type TagProps,
  EntityType,
  FAIconName,
  formatPhoneNumberForTextInputV2,
  formatAddressObjToStr,
  TagColor,
} from '@equip.health/ui';
import { isEqual, isNil, omitBy, startCase } from 'lodash';
import { DateTime } from 'luxon';

import { toLocalMidnight } from './date.util';
import {
  type ContactComparisonEntity,
  type CoverageComparisonEntity,
  type EitherComparisonEntity,
  type InquiryASFilterKeys,
  type InquiryFilterValue,
  type InquiryPSRFilterKeys,
  type NormalizeDuplicateResponseData,
  COMPARISON_FIELD_LABELS,
  coverageMergeFields,
  familyContactMergeFields,
  filterValuesMapper,
  INQUIRY_DATE_MIN_LIMIT_IN_MONTHS,
  InquiryFilterKeys,
  MergeConfirmationActionType,
  nonFamilyContactMergeFields,
} from '../constants/inquiry.constants';
import type {
  AssignedProvider,
  ContactInfoValue,
  ReferralInfoValue,
} from '~/components/common/inquiry/drawer/types';
import util from '~/lib/util/util';
import { PatientCoveragePriority } from './patientCoverage.util';
import { getDisplayNameOrLegalNameForProvider } from './user.util';
import type { GetProviderAvailabilityResponseV2 } from '~/store/services/schedule';
import { SYSTEM_TIME_ZONE } from './calendar.util';
import { v4 as uuidv4 } from 'uuid';

export const buildUniversalSelectSearchTerm = (s: string): string => {
  return s.replace(/ /g, '').toLowerCase();
};

export const getSelectedUniversalSelectOption = (
  value: string,
  options: UniversalSelectOption[],
): UniversalSelectOption => {
  return options.find((option) => option.value === value) ?? null;
};

export const dropdownMenuOptionsMapper = (
  options: string[],
): UniversalSelectOption[] => {
  return (options ?? []).map((option) => ({
    label: option,
    searchData: buildUniversalSelectSearchTerm(option),
    value: option,
  }));
};

export const inquiryEnumMenuOptionsMapper = (
  options: InquiryEnum[],
): UniversalSelectOption[] => {
  return (options ?? []).map(({ id, name }: InquiryEnum) => ({
    label: name,
    searchData: buildUniversalSelectSearchTerm(name),
    value: id,
  }));
};

/** Whether the equip source with `sourceId` requires a "tell us more" detail. */
export const isEquipSourceDetailRequired = (
  sources: EquipSourceV2[] | undefined,
  sourceId: string | undefined,
): boolean =>
  !!sources
    ?.find((source) => source.id === sourceId)
    ?.name.toLowerCase()
    .includes('please specify');

/**
 * Build the reducer update for an equip source change.
 * Returns `null` when the value hasn't changed (no update needed).
 * Clears `tellUsMore` only when the previous source showed the detail
 * input ("please specify") and the new source does not.
 */
export const buildEquipSourceUpdate = (
  sources: EquipSourceV2[] | undefined,
  currentSourceId: string | undefined,
  newSourceId: string,
): { equipSourceId: string; tellUsMore?: string } | null => {
  if (newSourceId === currentSourceId) return null;
  const shouldClearTellUsMore =
    isEquipSourceDetailRequired(sources, currentSourceId) &&
    !isEquipSourceDetailRequired(sources, newSourceId);
  return {
    equipSourceId: newSourceId,
    ...(shouldClearTellUsMore && { tellUsMore: '' }),
  };
};

export const equipSourceMenuOptionsMapper = (
  options: EquipSourceV2[],
  inquirerRole?: EquipInquirerRoleCategoryCode,
): UniversalSelectOption[] => {
  return (options ?? [])
    .filter(
      ({ inquirerRoleCategoryCode, isArchived }: EquipSourceV2) =>
        !isArchived && inquirerRoleCategoryCode === inquirerRole,
    )
    .map(({ id, name }: EquipSourceV2) => ({
      label: name,
      searchData: buildUniversalSelectSearchTerm(name),
      value: id,
    }));
};

export const getInquirerRoleCategoryCode = (
  inquirerRole: string,
): EquipInquirerRoleCategoryCode => {
  if (inquirerRole === 'Patient' || inquirerRole === 'Loved one') {
    return 'PLO';
  }

  if (inquirerRole === 'Referrer') {
    return 'REF';
  }

  return undefined;
};

export const getContactsWithRequiredFields = (
  arr: ContactInfoValue[],
): ContactInfoValue[] => {
  return (arr ?? [])?.filter(
    (item) => item.relationToPatient && item.firstName,
  );
};

export const getProvidersForAvatar = (
  assignedProviders: AssignedProvider[],
): AvatarProps[] =>
  assignedProviders.map(
    ({
      externalId,
      firstName,
      lastName,
      providerType,
      displayFirstName,
      displayLastName,
      avatar,
    }) => ({
      id: externalId,
      name: getDisplayNameOrLegalNameForProvider(
        displayFirstName,
        firstName,
        displayLastName,
        lastName,
      ),
      userType: providerType,
      src: avatar?.thumbnailUrl || avatar?.url,
    }),
  ) as AvatarProps[];

export const isAnyFieldFilled = (
  obj: ContactInfoValue | ReferralInfoValue,
): boolean => {
  for (const key of Object.keys(obj)) {
    if (
      key !== 'isInvalidEmail' &&
      key !== 'isInvalidCellPhone' &&
      key !== 'isPrimaryContact' &&
      key !== 'isInvalidOfficePhone' &&
      obj[key]
    ) {
      return true;
    }
  }
  return false;
};

export const validateInquiryRequiredFields = (obj: any): boolean => {
  for (const key in obj) {
    const val = obj[key];
    if (!val || (typeof val === 'string' && util.isEmptyString(val))) {
      return false;
    }
  }
  return true;
};

export const getReferrerTypesFromEnumeration = (
  enumerations: RelationToPatientEnumeration[],
) => {
  return [
    ...new Set(enumerations.map((enumeration) => enumeration.subcategory)),
  ];
};

export const subtractDaysAgoAndFormatISODate = (
  days: number,
  filterKey:
    | InquiryFilterKeys.LAST_CONTACT_DATE_MIN
    | InquiryFilterKeys.LAST_CONTACT_DATE_MAX,
  date = new Date(),
) => {
  const defaultZone = 'America/New_York';
  if (!days) {
    return DateTime.fromJSDate(date).setZone(defaultZone).toISO();
  }
  const daysAgo = DateTime.fromJSDate(date)
    .setZone(defaultZone)
    .minus({ days });
  if (filterKey === InquiryFilterKeys.LAST_CONTACT_DATE_MIN) {
    return daysAgo.startOf('day').toISO();
  }
  return daysAgo.endOf('day').toISO();
};

export const getDefaultInquiryDateMin = (): DateTime =>
  DateTime.now()
    .minus({ months: INQUIRY_DATE_MIN_LIMIT_IN_MONTHS })
    .toUTC()
    .startOf('day');

/** Use a default min date if the provided date is invalid or before the limit */
export const getInquiryDateMin = (date: string): DateTime => {
  const parsedDate = date ? DateTime.fromISO(date) : null;

  const absoluteMin = getDefaultInquiryDateMin();

  if (!parsedDate?.isValid || parsedDate.diff(absoluteMin).milliseconds <= 0) {
    return absoluteMin;
  }

  return parsedDate;
};

export function isEqualIgnoreNil<T>(a: T, b: T): boolean {
  return isEqual(omitBy(a, isNil), omitBy(b, isNil));
}

export function reducer<T>(state: T, action: T): T {
  return { ...state, ...action };
}

/**
 * Create a TagProps object from InquiryEnumV2 matching code.
 * @param code The code to find
 * @param enumValues List of InquiryEnumV2 objects.
 */
export const createTagProps = (
  code?: string,
  enumValues?: InquiryEnumV2[],
): TagProps => {
  const foundEnum = (enumValues ?? []).find(
    (enumValue) => enumValue.code === code,
  );
  return foundEnum
    ? {
        color: foundEnum.color as TagColor,
        id: foundEnum.code,
        value: foundEnum.description,
      }
    : null;
};

export const findCodeByDescription = (
  description: string,
  enumValues?: InquiryEnumV2[],
): EnumerationRequest => {
  return {
    code: (enumValues ?? []).find((e) => e.description === description)?.code,
  };
};

export const toDescriptions = (enumValues?: InquiryEnumV2[]): string[] =>
  (enumValues ?? []).map((e) => e.description);

export const toDate = (apiDateString?: string): Date => {
  return apiDateString && toLocalMidnight(new Date(apiDateString));
};

export const toStatusOptions = (enums: InquiryEnumV2[]) => {
  return (enums ?? []).map((o) => {
    return {
      color:
        (Object.keys(TagColor).find((c) => c === o.color) as TagColor) ||
        TagColor.gray,
      value: o.description,
    };
  });
};

export const formatOptionLabel = (
  option: MultiSelectOption,
  context: { context: string },
): boolean | ReactNode => {
  const { label, value, primaryText } = option;
  return context.context === 'menu' ? label : (primaryText ?? value);
};

export const getFilterValues = (
  filterElements: InquiryFilterValue,
  filterKey: InquiryPSRFilterKeys | InquiryASFilterKeys,
): TableFilter[] => {
  if (!filterElements || (filterElements as FilterElement[]).length === 0)
    return [];

  return [
    {
      filterKey,
      ...filterValuesMapper[filterKey](filterElements),
    },
  ];
};

export const InquiryFilterDropdownMenuOptionsMapper = (
  options: string[],
): DropdownOption[] => {
  return options.map((option) => ({
    label: option,
    value: option,
  }));
};

const normalizeValue = (val: string | number | string[]): string => {
  if (Array.isArray(val)) {
    return [...val].sort((a, b) => a.localeCompare(b)).join(', ');
  }
  const cleaned = typeof val === 'string' && val.replace(/[^\d+]/g, '');
  if (/^(\+1)?\d{10}$/.test(cleaned)) {
    return formatPhoneNumberForTextInputV2(cleaned);
  }

  return val?.toString() || '';
};

export const formatFields = (
  contact: PatientContact,
  fieldKeys: string[],
  omitEmailAndCellPhone: boolean = false,
): ComparisonField[] => {
  const addressField =
    fieldKeys.includes('address') && contact.address
      ? [
          {
            label: 'Address',
            value: formatAddressObjToStr(contact.address),
          },
        ]
      : [];
  const validFields = fieldKeys
    .filter(
      (key) =>
        key in contact &&
        key !== 'address' &&
        !(omitEmailAndCellPhone && ['cellPhone', 'email'].includes(key)),
    )
    .map((key) => ({
      label: COMPARISON_FIELD_LABELS[key] ?? startCase(key),
      value: normalizeValue(contact[key as keyof PatientContact]) || '',
    }));

  return [...validFields, ...addressField];
};

const isContactEntityFamily = (
  familyRelations: string[],
  relationToPatient: string,
): boolean => {
  return familyRelations?.includes(relationToPatient);
};

export const isContactEntity = (
  entityType: EntityType,
): entityType is EntityType.CONTACT | EntityType.REFERRER => {
  return (
    entityType === EntityType.CONTACT || entityType === EntityType.REFERRER
  );
};

export const createComparisonEntity = (
  baseEntity: PatientContact | PatientCoverageExpanded,
  entityType: EntityType,
  fieldKeys: string[],
  omitEmailAndCellPhone: boolean,
): EitherComparisonEntity => {
  const isContact = isContactEntity(entityType);
  const contact = baseEntity as PatientContact;
  const coverage = baseEntity as PatientCoverageExpanded;
  const rawTimestamp = coverage.updatedAt ?? coverage.createdAt;
  const timestamp = Number(rawTimestamp);
  const address = isContactEntity ? contact.address : undefined;
  const coverageLastUpdated = !isNaN(timestamp)
    ? new Date(timestamp * 1000).toISOString()
    : rawTimestamp;

  const sharedFields = {
    address,
    avatar: undefined,
    entityType,
    fields: formatFields(baseEntity, fieldKeys, omitEmailAndCellPhone),
    lastUpdated: contact.updatedAT ?? contact.createdAT ?? coverageLastUpdated,
    name: {
      chosenName: isContact ? (contact.chosenName ?? '') : '',
      firstName: isContact ? (contact.firstName ?? '') : (coverage.payor ?? ''),
      lastName: isContact ? (contact.lastName ?? '') : '',
      prefix: isContact ? (contact.prefix ?? '') : '',
      pronouns: isContact ? (contact.pronouns?.join(', ') ?? '') : '',
    },
  };

  if (isContact) {
    return {
      ...sharedFields,
      contactExternalId: contact.contactExternalId ?? '',
      supportExternalId: contact.supportExternalId ?? '',
    } as ContactComparisonEntity;
  }

  return {
    ...sharedFields,
    coverageExternalId: coverage.coverageExternalId ?? '',
  } as CoverageComparisonEntity;
};

const normalizeDuplicateEntityData = <
  DuplicateEntryData extends
    | DuplicateCoveragesData
    | GetDuplicateContactsResponseData,
  DuplicateEntryDataKey extends keyof DuplicateEntryData,
>({
  baseEntityKey,
  entityType,
  familyRelations,
  filterEntry = () => true,
  getFieldKeys,
  response,
}: NormalizeArgs<
  DuplicateEntryData,
  DuplicateEntryDataKey
>): NormalizeDuplicateResponseData[] => {
  if (!response?.data?.length) return [];

  const entries = response.data.filter(filterEntry);

  return entries.flatMap((entry) => {
    const sourceEntity = entry[baseEntityKey];
    const matches = entry.matches.map((m) => m[baseEntityKey]);

    const fieldKeys = getFieldKeys(sourceEntity);

    const omitEmailAndCellPhone =
      (sourceEntity as PatientContact).isSupport ||
      matches.some((m) => (m as PatientContact).isSupport);

    const isFamily = isContactEntityFamily(
      familyRelations,
      (sourceEntity as PatientContact)?.relationToPatient,
    );

    const baseEntity = createComparisonEntity(
      sourceEntity,
      entityType === EntityType.CONTACT && !isFamily
        ? EntityType.REFERRER
        : entityType,
      fieldKeys,
      omitEmailAndCellPhone,
    );

    const matchedEntities = matches.map((match) => {
      const isFamily = isContactEntityFamily(
        familyRelations,
        (match as PatientContact)?.relationToPatient,
      );
      return createComparisonEntity(
        match,
        entityType === EntityType.CONTACT && !isFamily
          ? EntityType.REFERRER
          : entityType,
        fieldKeys,
        omitEmailAndCellPhone,
      );
    });

    return matchedEntities.length > 0 ? [{ baseEntity, matchedEntities }] : [];
  });
};

export const normalizeDuplicateContactResponseData = (
  response: GetDuplicateContactsResponse,
  familyRelations: string[],
): NormalizeDuplicateResponseData[] => {
  if (response.status !== 'OK') return [];
  return normalizeDuplicateEntityData<
    GetDuplicateContactsResponseData,
    'contactOrSupport'
  >({
    baseEntityKey: 'contactOrSupport',
    entityType: EntityType.CONTACT,
    familyRelations,
    filterEntry: (entry: GetDuplicateContactsResponseData) =>
      !entry.contactOrSupport?.isSupport,
    getFieldKeys: (entity: PatientContact) => {
      const isFamily = isContactEntityFamily(
        familyRelations,
        entity?.relationToPatient,
      );
      const keys = isFamily
        ? familyContactMergeFields
        : nonFamilyContactMergeFields;
      return Object.keys(entity).filter((key) => keys.has(key));
    },
    response,
  });
};

export const normalizeDuplicateCoverageResponseData = (
  response: GetDuplicateCoveragesResponse,
): NormalizeDuplicateResponseData[] => {
  return normalizeDuplicateEntityData<DuplicateCoveragesData, 'coverage'>({
    baseEntityKey: 'coverage',
    entityType: EntityType.COVERAGE,
    getFieldKeys: (entity: PatientCoverageExpanded) =>
      Object.keys(entity).filter((key) => coverageMergeFields.has(key)),
    response,
  });
};

export const normalizeDuplicateResponseData = (
  response: GetDuplicateContactsResponse | GetDuplicateCoveragesResponse,
  entityType: EntityType,
  familyRelations: string[] = [],
): NormalizeDuplicateResponseData[] => {
  return isContactEntity(entityType)
    ? normalizeDuplicateContactResponseData(
        response as GetDuplicateContactsResponse,
        familyRelations,
      )
    : normalizeDuplicateCoverageResponseData(
        response as GetDuplicateCoveragesResponse,
      );
};

export const LABEL_TO_FIELD_KEY = Object.entries(
  COMPARISON_FIELD_LABELS,
).reduce(
  (acc: Record<string, string>, [key, label]) => {
    acc[label] = key;
    return acc;
  },
  {} as Record<string, string>,
);

export const convertToPatientContact = (
  record: ContactComparisonEntity,
): Partial<PatientContact> => {
  const { chosenName, firstName, lastName, prefix } = record.name;

  const patientContact: Partial<PatientContact> = {
    chosenName: chosenName && chosenName !== 'Not set' ? chosenName : '',
    firstName: firstName || '',
    lastName: lastName || '',
    prefix: prefix === 'Not set' ? '' : prefix,
  };

  const extractedFields: Record<string, string[] | undefined> = {
    languages: undefined,
    pronouns: undefined,
  };
  const officeExtension = record.fields.find(
    (f) => f.label === 'officeExtension',
  );

  if (officeExtension?.value === '') {
    officeExtension.value = '-1';
  }

  record.fields.forEach(({ label, value }) => {
    const originalKey = LABEL_TO_FIELD_KEY[label] ?? label;
    const sanitizedValue = value === 'Not set' ? '' : (value ?? null);

    if (originalKey === 'address' && typeof value === 'string') {
      if (value === formatAddressObjToStr(record.address)) {
        patientContact.address = record.address;
      }
    } else if (originalKey in extractedFields) {
      if (sanitizedValue !== undefined) {
        extractedFields[originalKey] =
          typeof sanitizedValue === 'string'
            ? sanitizedValue
                .split(',')
                .map((item) => item.trim())
                .filter((item) => item !== '')
            : sanitizedValue;
      }
    } else {
      patientContact[originalKey] = sanitizedValue;
    }
  });

  Object.entries(extractedFields).forEach(([key, value]) => {
    if (value !== undefined) {
      patientContact[key] = value;
    }
  });

  return patientContact;
};

const DATE_FIELDS = new Set(['startDate', 'endDate', 'subscriberDateOfBirth']);
const BOOLEAN_FIELDS = new Set(['isActive', 'isInNetwork']);

export const convertToPatientCoverage = (
  record: CoverageComparisonEntity,
): Partial<PatientCoverageExpanded> => {
  const coverage: Partial<PatientCoverageExpanded> = {};

  record.fields.forEach(({ label, value }) => {
    const originalKey = LABEL_TO_FIELD_KEY[label] ?? label;
    const sanitizedValue = value === 'Not set' ? '' : value;

    if (originalKey === 'coveragePriority') {
      coverage.coveragePriority =
        sanitizedValue && sanitizedValue !== ''
          ? PatientCoveragePriority[sanitizedValue]
          : -1;
    } else if (BOOLEAN_FIELDS.has(originalKey)) {
      (coverage as PatientCoverageExpanded)[originalKey] =
        sanitizedValue === 'true';
    } else if (DATE_FIELDS.has(originalKey) && sanitizedValue === '') {
      (coverage as PatientCoverageExpanded)[originalKey] = '1900-01-01';
    } else {
      (coverage as PatientCoverageExpanded)[originalKey] = sanitizedValue;
    }
  });

  return coverage;
};

export const formatMergeRequest = (
  mergedRecords: (MergeDuplicateContactsData | MergeDuplicateCoveragesData)[],
  inquiryId: string,
): MergeDuplicateContactsAndCoveragesRequestParams => {
  const contacts: MergeDuplicateContactsData[] = [];
  const coverages: MergeDuplicateCoveragesData[] = [];

  mergedRecords.forEach((record) => {
    if ((record as MergeDuplicateContactsData)?.sourceContactId) {
      contacts.push(record as MergeDuplicateContactsData);
    } else if ((record as MergeDuplicateCoveragesData)?.sourceCoverageId) {
      coverages.push(record as MergeDuplicateCoveragesData);
    }
  });

  return {
    body: {
      contacts,
      coverages,
    },
    inquiryId,
  };
};

export const formatMergeDuplicateCoveragesData = (
  destinationCoverageId: string,
  mergedRecord: CoverageComparisonEntity,
  sourceCoverageId: string,
): MergeDuplicateCoveragesData => ({
  destinationCoverageId,
  mergedEntity: convertToPatientCoverage(mergedRecord),
  sourceCoverageId,
});

export const formatMergeDuplicateContactsData = (
  mergedRecord: EitherComparisonEntity,
  sourceContactId: string,
  destinationContactId?: string,
  destinationSupportId?: string,
): MergeDuplicateContactsData => ({
  ...(destinationContactId ? { destinationContactId } : {}),
  ...(destinationSupportId ? { destinationSupportId } : {}),
  mergedEntity: convertToPatientContact(mergedRecord),
  sourceContactId,
});

export const mergeDuplicatesConfirmationActionConfig = (
  entityType: string,
) => ({
  [MergeConfirmationActionType.MERGE]: {
    confirmationDescription: `The ${entityType}s you reviewed will be merged into one record that will be visible on both the patient profile and the inquiry. Please note, this action is not reversible.`,
    confirmationTitle: `Confirm ${entityType} merge`,
    submitButtonIcon: FAIconName.mergeRegular,
    submitText: `Merge into one ${entityType}`,
  },
  [MergeConfirmationActionType.KEEP]: {
    confirmationDescription: `Both ${entityType}s will be visible on the patient profile. Please only keep both if you’re confident they don’t represent the same ${entityType}.`,
    confirmationTitle: `Keep both ${entityType}s`,
    submitButtonIcon: FAIconName.userGroupRegular,
    submitText: `Keep both ${entityType}s`,
  },
  default: {
    confirmationDescription:
      'Are you sure you want to close without making any changes?',
    confirmationTitle: 'Confirm close',
    submitButtonIcon: FAIconName.XmarkRegular,
    submitText: 'Close without changes',
  },
});

export const formatEntitiesForMerge = (
  mergedRecord: EitherComparisonEntity,
  recordA: EitherComparisonEntity,
  recordB: EitherComparisonEntity,
) => {
  const entityType = recordA.entityType;
  if (entityType === EntityType.COVERAGE) {
    return formatMergeDuplicateCoveragesData(
      (recordB as CoverageComparisonEntity).coverageExternalId,
      mergedRecord,
      (recordA as CoverageComparisonEntity).coverageExternalId,
    );
  }
  if (isContactEntity(entityType)) {
    return formatMergeDuplicateContactsData(
      mergedRecord,
      (recordA as ContactComparisonEntity).contactExternalId,
      (recordB as ContactComparisonEntity).contactExternalId,
      (recordB as ContactComparisonEntity).supportExternalId,
    );
  }
  return null;
};

export const getGroupedTimeSlots = (
  rawData: GetProviderAvailabilityResponseV2['availableSlots'],
  timeZone: string = SYSTEM_TIME_ZONE,
) => {
  const sortedData = [...rawData].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
  );

  const groupedMap = new Map();
  const allUserIds = new Set<string>();

  const todayDateKey = new Date().toLocaleDateString('en-CA', {
    timeZone,
  });

  const formatTimeRange = (startStr: string, endStr: string): string => {
    const start = DateTime.fromISO(startStr, { zone: timeZone });
    const end = DateTime.fromISO(endStr, { zone: timeZone });

    const startFmt = start.minute === 0 ? 'h' : 'h:mm';
    const endFmt = end.minute === 0 ? 'h' : 'h:mm';

    return `${start.toFormat(startFmt)} - ${end.toFormat(endFmt + 'a').toLowerCase()}`;
  };

  sortedData.forEach((slot) => {
    const startDateUtc = new Date(slot.start);
    const dateKey = startDateUtc.toLocaleDateString('en-CA', {
      timeZone,
    });

    if (!groupedMap.has(dateKey)) {
      const dateLabel = startDateUtc.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone,
      });

      const [y, m, d] = dateKey.split('-').map(Number);
      const [ty, tm, td] = todayDateKey.split('-').map(Number);
      const slotMidnight = new Date(y, m - 1, d);
      const todayMidnight = new Date(ty, tm - 1, td);
      const diffDays = Math.round(
        (slotMidnight.getTime() - todayMidnight.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      let relativeLabel = '';
      if (diffDays === 0) relativeLabel = 'Today';
      else if (diffDays === 1) relativeLabel = 'Tomorrow';
      else relativeLabel = `${diffDays} days away`;

      groupedMap.set(dateKey, {
        dateKey,
        dateLabel,
        relativeLabel,
        slots: [],
      });
    }

    const timeLabel = formatTimeRange(slot.start, slot.end);

    slot.users.forEach((user) => {
      allUserIds.add(user.id);

      groupedMap.get(dateKey).slots.push({
        originalStart: slot.start,
        originalEnd: slot.end,
        timeLabel: timeLabel,
        user_id: user.id,
        slotId: uuidv4(),
      });
    });
  });

  return {
    slots: Array.from(groupedMap.values()),
    user_ids: Array.from(allUserIds),
  };
};
